import React, { useEffect, useRef, useState, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import mapPoints from "../data/packraft-map-points.json";

const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const buildPopupHtml = (point) => {
  const imageBlock = point.image
    ? `<a href="${escapeHtml(point.image)}" target="_blank" rel="noopener noreferrer" class="packraft-map-popup__image-link"><img src="${escapeHtml(point.image)}" alt="${escapeHtml(point.imageAlt || point.title)}" class="packraft-map-popup__image" loading="lazy" /></a>`
    : "";

  return `<div class="packraft-map-popup">
    <strong class="packraft-map-popup__title">${escapeHtml(point.title)}</strong>
    ${point.subtitle ? `<span class="packraft-map-popup__subtitle">${escapeHtml(point.subtitle)}</span>` : ""}
    ${imageBlock}
  </div>`;
};

const getRouteLatLngs = () =>
  (mapPoints.route || []).map(([lat, lng]) => [lat, lng]);

const getBounds = () => {
  const latLngs = [
    ...mapPoints.points.map((p) => [p.lat, p.lng]),
    ...getRouteLatLngs(),
  ];
  return L.latLngBounds(latLngs).pad(mapPoints.boundsPadding);
};

function PackraftLeafletMap({ expanded = false, active = true }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!active || !containerRef.current) return undefined;

    const map = L.map(containerRef.current, {
      scrollWheelZoom: expanded,
      zoomControl: true,
    });

    L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      {
        attribution:
          'Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
        maxZoom: 18,
      }
    ).addTo(map);

    const routeLatLngs = getRouteLatLngs();
    if (routeLatLngs.length >= 2) {
      const style = mapPoints.routeStyle || {};
      L.polyline(routeLatLngs, {
        color: style.color || "#38bdf8",
        weight: style.weight ?? 4,
        opacity: style.opacity ?? 0.9,
        lineCap: "round",
        lineJoin: "round",
      }).addTo(map);
    }

    mapPoints.points.forEach((point) => {
      L.circleMarker([point.lat, point.lng], {
        radius: expanded ? 9 : 7,
        color: "#fff",
        weight: 2,
        fillColor: "#b45309",
        fillOpacity: 1,
      })
        .bindPopup(buildPopupHtml(point), { maxWidth: 280, minWidth: 180 })
        .addTo(map);
    });

    map.fitBounds(getBounds());
    mapRef.current = map;

    const resizeObserver = new ResizeObserver(() => {
      map.invalidateSize();
    });
    resizeObserver.observe(containerRef.current);

    const resizeTimer = window.setTimeout(() => map.invalidateSize(), 150);

    return () => {
      window.clearTimeout(resizeTimer);
      resizeObserver.disconnect();
      map.remove();
      mapRef.current = null;
    };
  }, [active, expanded]);

  return (
    <div
      ref={containerRef}
      className={`packraft-map__canvas${expanded ? " packraft-map__canvas--expanded" : ""}`}
    />
  );
}

const PackraftGrandCanyonMap = () => {
  const [expanded, setExpanded] = useState(false);

  const closeExpanded = useCallback(() => setExpanded(false), []);

  useEffect(() => {
    if (!expanded) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") closeExpanded();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [expanded, closeExpanded]);

  return (
    <>
      <figure className="packraft-map">
        <PackraftLeafletMap active={!expanded} />
        <figcaption className="packraft-map__caption">
          Colorado River route from Lee's Ferry to Pipe Creek (OpenStreetMap).
          Click a marker for details
          {mapPoints.points.some((p) => p.image) ? " and a photo" : ""}. Map data
          &copy;{" "}
          <a
            href="https://www.openstreetmap.org/copyright"
            target="_blank"
            rel="noopener noreferrer"
          >
            OpenStreetMap
          </a>{" "}
          contributors; imagery &copy; Esri.
        </figcaption>
        <div className="packraft-map__toolbar">
          <button
            type="button"
            className="packraft-map__expand"
            onClick={() => setExpanded(true)}
          >
            Enlarge map
          </button>
          {mapPoints.gpxUrl ? (
            <a
              href={mapPoints.gpxUrl}
              download
              className="packraft-map__gpx"
            >
              Download GPX
            </a>
          ) : null}
        </div>
      </figure>

      {expanded ? (
        <div
          className="packraft-map-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Enlarged Grand Canyon map"
        >
          <button
            type="button"
            className="packraft-map-modal__backdrop"
            aria-label="Close map"
            onClick={closeExpanded}
          />
          <div className="packraft-map-modal__panel">
            <button
              type="button"
              className="packraft-map-modal__close"
              aria-label="Close map"
              onClick={closeExpanded}
            >
              Close
            </button>
            <PackraftLeafletMap expanded active />
          </div>
        </div>
      ) : null}
    </>
  );
};

export default PackraftGrandCanyonMap;
